import { supabase } from "@/lib/supabase";

export interface EventRow {
  id: string;
  organizer_id: string;
  title: string;
  slug: string | null;
  event_date: string;
  location: string | null;
  description: string;
  expected_size: string;
  match_reveal_hours: number;
  reveal_immediately: boolean;
  status: "draft" | "active" | "completed" | "cancelled";
  created_at: string;
  participant_count?: number;
}

export interface CreateEventInput {
  title: string;
  event_date: string;
  location?: string;
  description: string;
  expected_size?: string;
  match_reveal_hours?: number;
}

// Description has no length cap — any non-empty value is accepted. The
// constants below stay exported so existing imports keep working; both
// resolve to permissive defaults so downstream UI degrades to "just
// require non-empty" instead of failing the build.
export const EVENT_DESCRIPTION_MIN = 1;
export const EVENT_DESCRIPTION_MAX = 100_000;

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40) +
    "-" + Math.random().toString(36).slice(2, 8);
}

export async function fetchMyEvents(): Promise<EventRow[]> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return [];

  const { data, error } = await supabase
    .from("events")
    .select("*, event_participants(count)")
    .eq("organizer_id", user.user.id)
    .order("event_date", { ascending: false });
  if (error) throw new Error(error.message);

  return (data ?? []).map((e: Record<string, unknown>) => ({
    ...e,
    participant_count: (e.event_participants as Array<{ count: number }>)?.[0]?.count ?? 0,
  })) as EventRow[];
}

export async function fetchEventById(id: string): Promise<EventRow | null> {
  const { data, error } = await supabase
    .from("events")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data as EventRow | null;
}

export async function fetchEventBySlug(slug: string): Promise<EventRow | null> {
  const { data, error } = await supabase
    .from("events")
    .select("*, event_participants(count)")
    .eq("slug", slug)
    .eq("status", "active")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  const row = data as Record<string, unknown>;
  return {
    ...(row as unknown as EventRow),
    participant_count:
      (row.event_participants as Array<{ count: number }>)?.[0]?.count ?? 0,
  };
}

export async function updateEvent(
  id: string,
  patch: Partial<
    Pick<
      EventRow,
      | "title"
      | "event_date"
      | "location"
      | "description"
      | "match_reveal_hours"
      | "reveal_immediately"
      | "status"
    >
  >,
): Promise<EventRow> {
  const { data, error } = await supabase
    .from("events")
    .update(patch)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data as EventRow;
}

export async function cancelEvent(id: string): Promise<void> {
  const { error } = await supabase
    .from("events")
    .update({ status: "cancelled" })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

export async function createEvent(input: CreateEventInput): Promise<EventRow> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not authenticated");

  const slug = slugify(input.title);

  const { data, error } = await supabase
    .from("events")
    .insert({
      organizer_id: user.user.id,
      title: input.title,
      slug,
      event_date: input.event_date,
      location: input.location || null,
      description: input.description,
      expected_size: input.expected_size || "up_to_50",
      match_reveal_hours: input.match_reveal_hours ?? 2,
      status: "active",
    })
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data as EventRow;
}
